<template>
  <div class="d-flex">
    <MainHeader />
    <testPortalBanner v-if="isTestPortal" />
    <SideMenu :studyData="studyData" :isTestPortal="isTestPortal" />
    <div class="content has-header" :class="{ 'has-test-portal-header': isTestPortal }">
      <p class="fs-22 mt-4  page-title">
        <b>Version {{isSiteTeamUser ? 'Assignments' : 'Management'}}</b>
      </p>
      <div class="d-flex justify-content-between">
        <NavBar :routes="NavBarRoutes" />
        <div v-if="shouldShowArchivedToggle" class="mt-24">
          <span class="pr-10">Show archived</span>
          <label for="toggle-archived" class="switch-toggle-btn">
            <input id="toggle-archived" v-model="showArchived"  type="checkbox" />
            <div class="slider"></div>
          </label>
        </div>
      </div>
      <router-view :showArchived="showArchived" :studyData="studyData" @toggleShowArchived="updateShowArchivedToggle" :studyId="studyId"></router-view>
    </div>
  </div>
</template>

<script>
import MainHeader from "@/components/common/main-header";
import SideMenu from "@/components/common/side-menu";
import NavBar from "@/components/common/nav-bar";
import TestPortalBanner from "@/components/common/test-portal-banner";
import localStorageService from "@/services/localStorage";
import { TEST_PORTAL_SUFFIX } from "@/constants/config";
import { mapGetters } from "vuex";
import { SYSTEM_ADMIN, STUDY_ADMIN, STUDY_MANAGER, PI, SUB_PI, STUDY_COORDINATOR } from "@/constants/authRoles";
import { AclRule } from "vue-acl";

export default {
  name: "version-management",
  components: {
    MainHeader,
    SideMenu,
    NavBar,
    TestPortalBanner
  },
  data() {
    return {
      studyData: {},
      showArchived: true, // Default state for the toggle
      shouldShowArchivedToggle: false, // Control visibility of the toggle
      tabs: [
        {
          label: "History",
          to: {
            name: `${this.isTestPortal ? TEST_PORTAL_SUFFIX : ""}version-history`,
            params: { studyId: this.$route.params.studyId }
          }
        },
        {
          label: "Assignments",
          to: {
            name: `${this.isTestPortal ? TEST_PORTAL_SUFFIX : ""}version-assignments`,
            params: { studyId: this.$route.params.studyId }
          }
        },
        {
          label: "Notes",
          to: {
            name: `${this.isTestPortal ? TEST_PORTAL_SUFFIX : ""}version-notes`,
            params: { studyId: this.$route.params.studyId }
          }
        }
      ]
    };
  },
  computed: {
    studyId() {
      return this.$route.params.studyId;
    },
    NavBarRoutes() {
      return this.tabs;
    },
    ...mapGetters(["user"]),
    userAccess() {
      return new AclRule(this.user.userRole).generate();
    },
    canSysAdminAccess() {
      this.$acl.change(`${SYSTEM_ADMIN}`);
      return this.$acl.check("userAccess");
    },
    canStudyAdminAccess() {
      this.$acl.change(`${STUDY_ADMIN}`);
      return this.$acl.check("userAccess");
    },
    canStudyManagerAccess() {
      this.$acl.change(`${STUDY_MANAGER}`);
      return this.$acl.check("userAccess");
    },
    canPiAccess() {
      this.$acl.change(`${PI}`);
      return this.$acl.check("userAccess");
    },
    canSubPiAccess() {
      this.$acl.change(`${SUB_PI}`);
      return this.$acl.check("userAccess");
    },
    canStudyCoordinatorAccess() {
      this.$acl.change(`${STUDY_COORDINATOR}`);
      return this.$acl.check("userAccess");
    },
    isSiteTeamUser() {
      return this.canPiAccess || this.canSubPiAccess || this.canStudyCoordinatorAccess;
    },
    isAdminUser() {
      return this.canSysAdminAccess || this.canStudyAdminAccess || this.canStudyManagerAccess;
    },
    canAccessVersionManagement() {
      return this.isAdminUser || this.isSiteTeamUser;
    }
  },
  created() {
    if (!this.canAccessVersionManagement) {
      this.$router.push({ name: "studies" });
    }

    if (this.isSiteTeamUser) {
      this.tabs = this.tabs.filter(nbr => {
        return nbr.label.toLowerCase() == "assignments";
      });
    }
  },
  mounted() {
    try {
      const studyDetailsData = localStorageService.getItem("studyDetailsData")[this.studyId];

      if (studyDetailsData) {
        this.studyData = studyDetailsData;
      } else {
        this.getStudyDetails();
      }
    } catch (e) {
      console.error(e);
      this.studyData = {};
    }
  },
  props: {
    isTestPortal: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    async getStudyDetails() {
      await this.$api.getStudyDataFact(this.studyId).then(response => {
        try {
          const studyDetailsData = localStorageService.getItem("studyDetailsData") || {};
          studyDetailsData[this.studyId] = response.body;
          localStorageService.setItem("studyDetailsData", studyDetailsData);
          this.studyData = response.body;
        } catch (error) {
          this.$toastr.error(error);
        }
      });
    },
    updateShowArchivedToggle(data) {
      // Update the showArchivedToggle state when emitted from child component
      this.shouldShowArchivedToggle = data.showToggle;
      if(data && data.showDefaultArchived === "OFF"){
        this.showArchived = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.content {
  width: 100%;
  padding: 20px;
}

.nav {
  background: none !important;
  border-bottom: none !important;
}

.page-title {
  color: #2d3752;
}

/* toggle btn css - START */
.switch-toggle-btn {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 23px;
  top: -2px;
  &:hover {
    cursor: pointer;
  }
}
.switch-toggle-btn input {
  opacity: 0;
}

.switch-toggle-btn .slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #b0bac9;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 16px;
}

.switch-toggle-btn .slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 4px;
  top: 3px;
  background-color: #fff;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 50%;
}

.switch-toggle-btn input:checked + .slider {
  background-color: #4c8ce4;
}

.switch-toggle-btn input:focus + .slider {
  box-shadow: 0 0 1px #4c8ce4;
}

.switch-toggle-btn input:checked + .slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 2px;
  bottom: -1px;
  top: 3px;
  background-color: #fff;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 50%;
  -webkit-transform: translateX(14px);
  -ms-transform: translateX(14px);
  transform: translateX(14px);
}

.switch-toggle-btn .text:after {
  position: absolute;
  top: 1px;
  right: 10px;
  content: "";
  color: #fff;
  -webkit-transition: opacity 0.5s ease-in-out;
  -moz-transition: opacity 0.5s ease-in-out;
  transition: opacity 0.5s ease-in-out;
}

.switch-toggle-btn .text:before {
  position: absolute;
  top: 1px;
  left: 10px;
  content: "";
  color: #fff;
  -webkit-transition: opacity 0.5s ease-in-out;
  -moz-transition: opacity 0.5s ease-in-out;
  transition: opacity 0.5s ease-in-out;
}

.switch-toggle-btn input + .slider + .text:after {
  opacity: 1;
}

.switch-toggle-btn input + .slider + .text:before {
  opacity: 0;
}

.switch-toggle-btn input:checked + .slider + .text:after {
  opacity: 0;
}

.switch-toggle-btn input:checked + .slider + .text:before {
  opacity: 1;
}

</style>
